// lib
import React from 'react'
import { List } from 'immutable'
import { toArray as rxToArray } from 'rxjs/operators'
import { EditorState, ContentState, CharacterMetadata, SelectionState, Modifier, ContentBlock, genKey } from 'draft-js'
import { KEYCODE_UP, KEYCODE_DOWN, KEYCODE_ESC, KEYCODE_ENTER } from '@aytm/lib/common/const'

import suggestionsBuilder from './suggestions-builder'
import defaultProcessText from './lib/default-process-text'
import CompoundDecorator from './decorators/compound-decorator'
import DefaultSuggestionsComponent from './components/Suggestions'

const createDefaultSuggestions = () => {
  return {
    isActive: false,
    doneFetching: false,
    selection: null,
    items: []
  }
}

const preFillSuggestions = selection => {
  return {
    isActive: true,
    doneFetching: false,
    items: [],
    selection
  }
}

const countLines = text => {
  const matchNewlines = text.match(/\n/g)
  return matchNewlines ? matchNewlines.length : 1
}

export const extractSelectionOffsets = editorState => {
  const selectionState = editorState.getSelection()

  const anchorKey = selectionState.getAnchorKey()
  const focusKey = selectionState.getFocusKey()
  const blockMap = editorState.getCurrentContent().getBlockMap()

  if (!blockMap.has(focusKey)) {
    return null
  }

  let startOffset = 0
  let endOffset = 0

  blockMap.skipWhile((contentBlock, key) => {
    if (key === anchorKey) {
      startOffset += selectionState.getAnchorOffset()
      return false
    }
    // textBefore.push(contentBlock.getText())
    startOffset += contentBlock.getText().length + 1
    return true
  })

  blockMap.skipWhile((contentBlock, key) => {
    if (key === focusKey) {
      endOffset += selectionState.getFocusOffset()
      return false
    }
    endOffset += contentBlock.getText().length + 1
    return true
  })

  return { startOffset, endOffset }
}

const getSuggestionsStub = () => {
  return {}
}
const createSuggestionsPlugin = (
  pluginStore,
  { customProcessText, extractSelection, suggestionsBuilders = [], SuggestionsComponent }
) => {
  const ref = React.createRef()
  const store = {
    getEditorState: null,
    setEditorState: null,
    subscription: null,
    suggestions: createDefaultSuggestions()
  }

  const getSuggestionsData = (beforeCursor, afterCursor) =>
    'function' === typeof pluginStore.getSuggestionsData
      ? pluginStore.getSuggestionsData(beforeCursor, afterCursor)
      : getSuggestionsStub

  const processText = 'function' === typeof customProcessText ? customProcessText : defaultProcessText

  const checkAutocompleteIsVisible = () =>
    Boolean(
      store.suggestions.isActive &&
      store.suggestions.items &&
      store.suggestions.items.length &&
      store.suggestions.doneFetching
    )
  const handleWhenIsActive = () => (store.suggestions.isActive ? 'handled' : 'not-handled')

  const updateSuggestionsComponentItems = () => {
    ref.current && ref.current.updateItems(store.suggestions.items, checkAutocompleteIsVisible())
  }

  const selectSuggestion = ({ textBefore, textAfter }) => {
    const {
      suggestions: { selection }
    } = store
    if (!selection) return // the dropdown's window listener outlives a store reset by a render

    const editorState = store.getEditorState()
    const contentState = editorState.getCurrentContent()
    const plainText = contentState.getPlainText()

    const newPlainTextBefore = plainText.slice(0, selection.range.start) + textBefore
    const newPlainTextAfter = textAfter + plainText.slice(selection.range.end + selection.text.afterCursor.length)

    const newPlainText =
      countLines(plainText) != countLines(newPlainTextBefore + newPlainTextAfter)
        ? newPlainTextBefore + '\n' + newPlainTextAfter
        : newPlainTextBefore + newPlainTextAfter

    const charsStyles = []

    contentState
      .getBlocksAsArray()
      .forEach(block =>
        block.getCharacterList().forEach(charMetaData => charsStyles.push(charMetaData.getStyle().toJS()))
      )

    const newCharsStyles = charsStyles
      .slice(0, selection.range.start)
      .concat(
        Array.from({ length: textBefore.length + textAfter.length }).fill([]),
        charsStyles.slice(selection.range.end + selection.text.afterCursor.length)
      )

    const characterList = List(Array.from({ length: newPlainText.length }).map(() => CharacterMetadata.create()))
    const newContentBlock = new ContentBlock({
      key: genKey(),
      text: newPlainText,
      characterList,
      type: 'unstyled',
      depth: 0
    })
    const newContentState = ContentState.createFromBlockArray([newContentBlock])
    const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters')

    const newCurrentContent = newEditorState.getCurrentContent()
    const newBlockArray = newCurrentContent.getBlocksAsArray()

    let cursorOffset = newPlainTextBefore.length
    let newAnchorKey

    while (newBlockArray.length) {
      const contentBlock = newBlockArray.shift()
      const text = contentBlock.getText()

      if (cursorOffset > text.length) {
        cursorOffset -= text.length + (text.match(/\r?\n/g) || '').length + 1
      } else {
        newAnchorKey = contentBlock.key
        break
      }
    }

    let styledCurrentContent = newCurrentContent
    let blockCursor = 0

    // applying inline styles to newCurrentContent
    newContentState.getBlocksAsArray().forEach(block => {
      let characterCursor = 0

      block.getCharacterList().forEach(_ => {
        const styles = newCharsStyles[blockCursor + characterCursor]
        if (styles && styles.length) {
          styles.forEach(
            inlineStyle =>
              (styledCurrentContent = Modifier.applyInlineStyle(
                styledCurrentContent,
                SelectionState.createEmpty(block.getKey()).merge({
                  anchorOffset: characterCursor,
                  focusOffset: characterCursor + 1
                }),
                inlineStyle
              ))
          )
        }
        characterCursor++
      })

      blockCursor += characterCursor
    })

    const styledEditorState = EditorState.createWithContent(styledCurrentContent, editorState.getDecorator())
    const styledSelectionState = newEditorState.getSelection().merge({
      anchorKey: newAnchorKey,
      focusKey: newAnchorKey,
      anchorOffset: cursorOffset,
      focusOffset: cursorOffset
    })

    store.setEditorState(EditorState.forceSelection(styledEditorState, styledSelectionState))
  }

  const createSuggestions = (selection, callback) => {
    // @TODO: refactor. make async with rx buffer
    const {
      text: { beforeCursor, afterCursor, meta }
    } = selection
    const suggestionsData = getSuggestionsData(beforeCursor, afterCursor)
    const processTextBeforeCursor = processText(beforeCursor, suggestionsData)
    const processTextAfterCursor = processText(afterCursor, suggestionsData)

    const suggestionsStream = suggestionsBuilder(suggestionsBuilders, processTextBeforeCursor, processTextAfterCursor, {
      dependencies: suggestionsData,
      meta: meta || {}
    }).pipe(rxToArray())

    store.suggestions = preFillSuggestions(selection)

    if (store.subscription) {
      store.subscription.unsubscribe()
      store.subscription = null
    }

    // async check done
    store.subscription = suggestionsStream.subscribe(
      items => {
        store.suggestions.error = null
        store.suggestions.items = items
      },
      error => {
        store.suggestions.error = error
        store.suggestions.items = []
        callback && callback()
      },
      _done => {
        store.suggestions.doneFetching = true
        callback && callback()
      }
    )
  }

  const canAutocompleteBeActive = () =>
    'function' === typeof pluginStore.canAutocompleteBeActive ? pluginStore.canAutocompleteBeActive() : true

  return {
    // eslint-disable-next-line react/display-name
    SuggestionsComponent: class extends React.PureComponent {
      render() {
        const Component = SuggestionsComponent || DefaultSuggestionsComponent
        return (
          <Component
            {...this.props}
            ref={el => {
              this.refComponent = el
              ref.current = el
            }}
            items={store.suggestions.items}
            visibility={checkAutocompleteIsVisible()}
            selectSuggestion={selectSuggestion}
          />
        )
      }
    },
    getCursor: () => {
      const editorState = store.getEditorState()
      const contentState = editorState.getCurrentContent()
      const selectionState = editorState.getSelection()

      return {
        text: contentState.getPlainText(),
        selection: {
          start: selectionState.getStartOffset(),
          end: selectionState.getEndOffset()
        }
      }
    },
    initialize: ({ getEditorState, setEditorState }) => {
      store.getEditorState = getEditorState
      store.setEditorState = setEditorState
    },
    decorators: pluginStore.decorators ? [new CompoundDecorator(pluginStore.decorators)] : [],
    handleReturn: handleWhenIsActive,
    keyBindingFn: event => {
      switch (event.which) {
        case KEYCODE_ESC: {
          if (checkAutocompleteIsVisible()) {
            store.suggestions.isActive = false
            updateSuggestionsComponentItems()
            if (event.preventDefault) event.preventDefault()
            if (event.stopPropagation) event.stopPropagation()
            return 'suggestions-close'
          }
          break
        }
        case KEYCODE_ENTER: {
          if (checkAutocompleteIsVisible()) {
            return 'suggestions-select'
          }
          break
        }
        case KEYCODE_UP:
        case KEYCODE_DOWN: {
          if (checkAutocompleteIsVisible()) {
            return 'suggestions-navigate'
          }
          break
        }
      }
      return void 0
    },
    handleKeyCommand: command => {
      if (checkAutocompleteIsVisible()) {
        switch (command) {
          case 'suggestions-close':
          case 'suggestions-select':
          case 'suggestions-navigate': {
            return 'handled'
          }
          default: {
            return 'not-handled'
          }
        }
      }
      return 'not-handled'
    },
    onChange: editorState => {
      if (!canAutocompleteBeActive()) {
        if (store.suggestions.isActive) {
          store.suggestions = createDefaultSuggestions()
          updateSuggestionsComponentItems()
        }

        return editorState
      }

      const selectionState = editorState.getSelection()

      if (!selectionState.getHasFocus()) {
        if (store.subscription) {
          store.subscription.unsubscribe()
          store.subscription = null
        }
        if (store.suggestions.isActive) {
          store.suggestions = createDefaultSuggestions()
          updateSuggestionsComponentItems()
        }

        return editorState
      }

      const nextSelection = extractSelection(extractSelectionOffsets(editorState), editorState)

      if (nextSelection) {
        createSuggestions(nextSelection, () => {
          updateSuggestionsComponentItems()
        })
      } else if (store.suggestions.isActive) {
        if (store.subscription) {
          store.subscription.unsubscribe()
          store.subscription = null
        }

        store.suggestions = createDefaultSuggestions()
        updateSuggestionsComponentItems()
      }

      return editorState
    }
  }
}

export default createSuggestionsPlugin
